import React, { createContext, useContext, useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { ConfigProvider, theme as antdTheme } from 'antd'
import { Chart } from 'chart.js'
import { PALETTES, DEFAULT_PALETTE, DEFAULT_MODE } from './palettes'

const STORAGE_KEY = 'cedars-admin-theme'
export const FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

// Only choices the user made are stored: a palette they picked (`paletteChosen`) always wins over
// their campus's default palette (see setDefaultPalette).
const read = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    const chosen = !!(saved.paletteChosen && PALETTES[saved.palette])
    return {
      palette: chosen ? saved.palette : DEFAULT_PALETTE,
      paletteChosen: chosen,
      mode: saved.mode === 'dark' || saved.mode === 'light' ? saved.mode : DEFAULT_MODE,
    }
  } catch (e) {
    return { palette: DEFAULT_PALETTE, paletteChosen: false, mode: DEFAULT_MODE }
  }
}

const save = (state) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ mode: state.mode, ...(state.paletteChosen ? { palette: state.palette, paletteChosen: true } : {}) }),
    )
  } catch (e) {
    // Storage unavailable (private mode): the theme still applies for this session.
  }
}

const hexToRgb = (hex) => {
  const n = parseInt(hex.replace('#', ''), 16)
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
}

/**
 * Writes the palette and mode onto <html> synchronously, so anything that reads CSS variables
 * during the next render (charts via getStyle) already sees the new values.
 */
export const applyTheme = ({ palette, mode }) => {
  const p = PALETTES[palette]
  const root = document.documentElement
  root.setAttribute('data-theme', mode)
  root.style.setProperty('--app-primary', p.primary)
  root.style.setProperty('--app-primary-rgb', hexToRgb(p.primary))
  root.style.setProperty('--app-primary-hover', p.primaryHover)
  root.style.setProperty('--app-accent', p.accent)
  root.style.setProperty('--app-accent-rgb', hexToRgb(p.accent))
  root.style.setProperty('--app-sidebar-bg', mode === 'dark' ? p.sidebarDark : p.sidebar)

  // Chart.js reads these for axis labels, legends and gridlines.
  const dark = mode === 'dark'
  Chart.defaults.color = dark ? 'rgba(226, 232, 240, 0.75)' : 'rgba(30, 41, 59, 0.7)'
  Chart.defaults.borderColor = dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.06)'
  Chart.defaults.font.family = FONT_FAMILY
}

const ThemeContext = createContext({})
export const useTheme = () => useContext(ThemeContext)

export default function ThemeProvider({ children }) {
  const [state, setState] = useState(() => {
    const initial = read()
    applyTheme(initial)
    return initial
  })

  const update = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch }
      applyTheme(next)
      save(next)
      return next
    })
  }, [])

  const value = useMemo(
    () => ({
      ...state,
      // Changes whenever the theme does; charts use it as a key to redraw with new colours.
      themeKey: `${state.palette}-${state.mode}`,
      setPalette: (palette) => update({ palette, paletteChosen: true }),
      // The campus's default palette, used until the user picks one themselves (not saved).
      setDefaultPalette: (palette) =>
        setState((prev) => {
          if (prev.paletteChosen || prev.palette === palette || !PALETTES[palette]) return prev
          const next = { ...prev, palette }
          applyTheme(next)
          return next
        }),
      setMode: (mode) => update({ mode }),
      toggleMode: () => update({ mode: state.mode === 'dark' ? 'light' : 'dark' }),
    }),
    [state, update],
  )

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        theme={{
          algorithm: state.mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          token: {
            colorPrimary: PALETTES[state.palette].primary,
            fontFamily: FONT_FAMILY,
            borderRadius: 8,
          },
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  )
}

ThemeProvider.propTypes = { children: PropTypes.node }
